import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Mail, Phone, RefreshCw, Sparkles, Copy } from "lucide-react";
import Logo from "../components/Logo";
import { EASE } from "../components/Reveal";
import ContentEditor from "./ContentEditor";
import VouchersManager from "./VouchersManager";
import ActivityManager from "./ActivityManager";
import TrackingManager from "./TrackingManager";
import TraitorsManager from "./TraitorsManager";
import GalleryManager from "./GalleryManager";
import InstagramConnect from "./InstagramConnect";
import JournalManager from "./JournalManager";
import AvailabilityManager from "./AvailabilityManager";
import { motion } from "framer-motion";

import API from "../api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-IE", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const fmtDay = (d) =>
  new Date(`${d}T12:00:00`).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" });

function LoginCard({ onAuth }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      onAuth(data);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: EASE }}
      className="w-full max-w-md border border-gold/25 bg-navy-2 p-8 md:p-10"
      data-testid="admin-login-card"
    >
      <Logo className="mx-auto h-12 w-12" />
      <h1 className="mt-6 text-center font-display text-3xl text-ivory">Enquiries Inbox</h1>
      <p className="mt-2 text-center text-xs uppercase tracking-widest2 text-coastal">Collanmore Island Lodge</p>
      <form onSubmit={submit} className="mt-8 flex flex-col gap-5" data-testid="admin-login-form">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-ivory/60" htmlFor="admin-email">
            Email
          </label>
          <input
            id="admin-email"
            type="email"
            required
            data-testid="admin-login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full border-b border-ivory/25 bg-transparent py-2.5 text-sm font-light text-ivory outline-none transition-colors focus:border-gold"
          />
        </div>
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-ivory/60" htmlFor="admin-password">
            Password
          </label>
          <input
            id="admin-password"
            type="password"
            required
            data-testid="admin-login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 w-full border-b border-ivory/25 bg-transparent py-2.5 text-sm font-light text-ivory outline-none transition-colors focus:border-gold"
          />
        </div>
        {error && (
          <p className="text-xs text-red-400" data-testid="admin-login-error">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-button"
          className="mt-2 bg-gold py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-navy transition-colors hover:bg-gold-2 disabled:opacity-60"
        >
          {busy ? "Checking…" : "Sign In"}
        </button>
      </form>
    </motion.div>
  );
}

function EnquiryRow({ eq }) {
  const isNew = eq.status === "new";
  return (
    <div
      className={`border border-ivory/10 bg-navy-2 p-6 md:p-7 ${isNew ? "border-l-2 border-l-gold" : ""}`}
      data-testid={`admin-enquiry-${eq.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-display text-2xl text-ivory" data-testid={`admin-enquiry-name-${eq.id}`}>
            {eq.full_name}
          </p>
          <p className="mt-1 text-[11px] uppercase tracking-widest2 text-coastal">
            {fmtDate(eq.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isNew && (
            <span className="bg-gold px-3 py-1 text-[10px] font-semibold uppercase tracking-widest2 text-navy" data-testid={`admin-enquiry-new-${eq.id}`}>
              New
            </span>
          )}
          <span className="border border-gold/40 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest2 text-gold-2">
            {eq.occasion}
          </span>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <p className="text-sm font-light text-ivory/80">
          <span className="block text-[10px] uppercase tracking-widest2 text-ivory/45">Stay</span>
          {fmtDay(eq.arrival_date)} → {fmtDay(eq.departure_date)}
        </p>
        <p className="text-sm font-light text-ivory/80">
          <span className="block text-[10px] uppercase tracking-widest2 text-ivory/45">Guests</span>
          {eq.guests}
        </p>
        <p className="text-sm font-light text-ivory/80">
          <span className="block text-[10px] uppercase tracking-widest2 text-ivory/45">Contact</span>
          <a href={`mailto:${eq.email}`} className="inline-flex items-center gap-1.5 text-gold-2 hover:underline">
            <Mail className="h-3.5 w-3.5" /> {eq.email}
          </a>
          <a href={`tel:${eq.phone}`} className="mt-1 flex items-center gap-1.5 text-gold-2 hover:underline">
            <Phone className="h-3.5 w-3.5" /> {eq.phone}
          </a>
        </p>
      </div>
      {eq.message && (
        <p className="mt-5 border-t border-ivory/10 pt-4 text-sm font-light italic leading-relaxed text-ivory/70" data-testid={`admin-enquiry-message-${eq.id}`}>
          “{eq.message}”
        </p>
      )}
      <AiDraft email={eq.email} id={eq.id} />
    </div>
  );
}

function AiDraft({ id, email }) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const make = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/enquiries/${id}/draft-reply`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setDraft(data.draft);
    } catch (e) {
      toast.error(e.message || "Could not draft a reply");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="mt-5 border-t border-ivory/10 pt-4">
      {!draft && !busy && (
        <button
          onClick={make}
          data-testid={`admin-draft-${id}`}
          className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest2 text-gold-2 transition-colors hover:text-gold"
        >
          <Sparkles className="h-3.5 w-3.5" /> Draft a reply with AI
        </button>
      )}
      {busy && (
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest2 text-coastal" data-testid={`admin-drafting-${id}`}>
          <Sparkles className="h-3.5 w-3.5 animate-pulse" /> Writing your reply…
        </p>
      )}
      {draft && (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows="5"
            data-testid={`admin-draft-text-${id}`}
            className="w-full resize-none border border-ivory/20 bg-navy p-4 text-sm font-light leading-relaxed text-ivory outline-none focus:border-gold"
          />
          <div className="mt-2 flex gap-3">
            <button
              onClick={() => { setDraft(""); }}
              data-testid={`admin-draft-redo-${id}`}
              className="text-[10px] uppercase tracking-widest2 text-coastal hover:text-gold-2"
            >
              Draft again
            </button>
            <button
              onClick={copy}
              data-testid={`admin-draft-copy-${id}`}
              className="flex items-center gap-1.5 bg-gold px-4 py-2 text-[10px] font-semibold uppercase tracking-widest2 text-navy hover:bg-gold-2"
            >
              <Copy className="h-3 w-3" /> {copied ? "Copied" : "Copy — then reply to the guest by email"}
            </button>
          </div>
          <p className="mt-2 text-[10px] font-light text-ivory/40">
            Reply to {email} from your own inbox after copying.
          </p>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [enquiries, setEnquiries] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const loadEnquiries = async () => {
    setLoadingList(true);
    try {
      const res = await fetch(`${API}/api/admin/enquiries`, { credentials: "include" });
      if (res.status === 401) {
        setUser(false);
        return;
      }
      const data = await res.json();
      setEnquiries(data.enquiries || []);
    } catch {
      toast.error("Could not load enquiries");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/me`, { credentials: "include" });
        if (res.ok) {
          setUser(await res.json());
          loadEnquiries();
        } else {
          setUser(false);
        }
      } catch {
        setUser(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    const t = setInterval(loadEnquiries, 30000);
    return () => clearInterval(t);
  }, [user]);

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    setUser(false);
    setEnquiries([]);
  };

  return (
    <div className="min-h-screen bg-navy px-5 py-10 md:px-10">
      {checking ? (
        <div className="flex min-h-[70vh] items-center justify-center">
          <Logo className="h-14 w-14 animate-pulse" />
        </div>
      ) : !user ? (
        <div className="flex min-h-[80vh] items-center justify-center">
          <LoginCard onAuth={(u) => { setUser(u); loadEnquiries(); }} />
        </div>
      ) : (
        <AdminTabs
          user={user}
          enquiries={enquiries}
          loadingList={loadingList}
          loadEnquiries={loadEnquiries}
          logout={logout}
        />
      )}
    </div>
  );
}

function AdminTabs({ user, enquiries, loadingList, loadEnquiries, logout }) {
  const [tab, setTab] = useState("enquiries");
  return (
    <div className="mx-auto max-w-5xl">
      <header className="flex flex-wrap items-center justify-between gap-4 border-b border-gold/20 pb-6">
        <div className="flex items-center gap-4">
          <Logo className="h-11 w-11" />
          <div>
            <h1 className="font-display text-3xl text-ivory" data-testid="admin-title">Admin</h1>
            <p className="text-[11px] uppercase tracking-widest2 text-coastal" data-testid="admin-user-email">
              {user.email}
            </p>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="admin-logout-button"
          className="flex items-center gap-2 border border-ivory/25 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:border-red-400 hover:text-red-300"
        >
          <LogOut className="h-3.5 w-3.5" /> Log out
        </button>
      </header>

      <nav className="mt-8 flex flex-wrap gap-2" aria-label="Admin sections">
        <button
          onClick={() => setTab("enquiries")}
          data-testid="admin-tab-enquiries"
          className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${tab === "enquiries" ? "bg-gold text-navy" : "border border-ivory/25 text-ivory/80 hover:text-gold-2"}`}
        >
          Enquiries
        </button>
        <button
          onClick={() => setTab("content")}
          data-testid="admin-tab-content"
          className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${tab === "content" ? "bg-gold text-navy" : "border border-ivory/25 text-ivory/80 hover:text-gold-2"}`}
        >
          Site Content
        </button>
        <button
          onClick={() => setTab("gallery")}
          data-testid="admin-tab-gallery"
          className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${tab === "gallery" ? "bg-gold text-navy" : "border border-ivory/25 text-ivory/80 hover:text-gold-2"}`}
        >
          Gallery
        </button>
        <button
          onClick={() => setTab("instagram")}
          data-testid="admin-tab-instagram"
          className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${tab === "instagram" ? "bg-gold text-navy" : "border border-ivory/25 text-ivory/80 hover:text-gold-2"}`}
        >
          Instagram
        </button>
        <button
          onClick={() => setTab("journal")}
          data-testid="admin-tab-journal"
          className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${tab === "journal" ? "bg-gold text-navy" : "border border-ivory/25 text-ivory/80 hover:text-gold-2"}`}
        >
          Journal
        </button>
        <button
          onClick={() => setTab("availability")}
          data-testid="admin-tab-availability"
          className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${tab === "availability" ? "bg-gold text-navy" : "border border-ivory/25 text-ivory/80 hover:text-gold-2"}`}
        >
          Availability
        </button>
        <button
          onClick={() => setTab("vouchers")}
          data-testid="admin-tab-vouchers"
          className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${tab === "vouchers" ? "bg-gold text-navy" : "border border-ivory/25 text-ivory/80 hover:text-gold-2"}`}
        >
          Vouchers
        </button>
        <button
          onClick={() => setTab("activities")}
          data-testid="admin-tab-activities"
          className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${tab === "activities" ? "bg-gold text-navy" : "border border-ivory/25 text-ivory/80 hover:text-gold-2"}`}
        >
          Activities
        </button>
        <button
          onClick={() => setTab("tracking")}
          data-testid="admin-tab-tracking"
          className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${tab === "tracking" ? "bg-gold text-navy" : "border border-ivory/25 text-ivory/80 hover:text-gold-2"}`}
        >
          Tracking
        </button>
        <button
          onClick={() => setTab("traitors")}
          data-testid="admin-tab-traitors"
          className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-widest2 transition-colors ${tab === "traitors" ? "bg-gold text-navy" : "border border-ivory/25 text-ivory/80 hover:text-gold-2"}`}
        >
          Traitors
        </button>
      </nav>

      {tab === "enquiries" ? (
        <div className="pb-20">
          <div className="mt-6 flex items-center justify-between gap-4">
            <p className="text-xs uppercase tracking-widest2 text-coastal" data-testid="admin-count">
              {enquiries.length} {enquiries.length === 1 ? "enquiry" : "enquiries"} · newest first · auto-refreshes
            </p>
            <button
              onClick={loadEnquiries}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 border border-ivory/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:border-gold hover:text-gold-2"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loadingList ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
          <div className="mt-6 flex flex-col gap-4" data-testid="admin-enquiry-list">
            {enquiries.length === 0 && !loadingList && (
              <p className="mt-16 text-center font-display text-2xl italic text-ivory/50" data-testid="admin-empty">
                No enquiries yet — they will appear here the moment a guest writes.
              </p>
            )}
            {enquiries.map((eq) => (
              <EnquiryRow key={eq.id} eq={eq} />
            ))}
          </div>
        </div>
      ) : tab === "content" ? (
        <div className="pb-20">
          <ContentEditor />
        </div>
      ) : tab === "gallery" ? (
        <div className="pb-20">
          <GalleryManager />
        </div>
      ) : tab === "journal" ? (
        <div className="pb-20">
          <JournalManager />
        </div>
      ) : tab === "availability" ? (
        <div className="pb-20">
          <AvailabilityManager />
        </div>
      ) : tab === "vouchers" ? (
        <VouchersManager />
      ) : tab === "activities" ? (
        <ActivityManager />
      ) : tab === "tracking" ? (
        <TrackingManager />
      ) : tab === "traitors" ? (
        <TraitorsManager />
      ) : tab === "instagram" ? (
        <div className="pb-20">
          <InstagramConnect />
        </div>
      ) : null}
    </div>
  );
}
